import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Check, Download, Phone, Play, ArrowRight } from 'lucide-react';
import BandInUseImage from '../assets/images/BandInUse.png';

const comparativeData = [
  { feature: 'Procedure Time', EBG: '30-45 min', IntragastricBalloons: '20-30 min', GLP1Inhibitors: 'N/A (medication)', SurgicalSleeveGastrectomy: '1-2 hours' },
  { feature: 'Invasiveness', EBG: 'Minimally invasive, endoscopic', IntragastricBalloons: 'Minimally invasive, endoscopic', GLP1Inhibitors: 'Non-invasive (injection)', SurgicalSleeveGastrectomy: 'Surgical, laparoscopic' },
  { feature: 'Durability', EBG: 'Long-term', IntragastricBalloons: 'Temporary (6-12 months)', GLP1Inhibitors: 'Requires ongoing treatment', SurgicalSleeveGastrectomy: 'Permanent' },
  { feature: 'Reversibility', EBG: 'Potentially reversible', IntragastricBalloons: 'Fully reversible', GLP1Inhibitors: 'Fully reversible', SurgicalSleeveGastrectomy: 'Not reversible' },
  { feature: 'Side Effects', EBG: 'Minimal, transient', IntragastricBalloons: 'Nausea, discomfort', GLP1Inhibitors: 'GI issues, potential long-term risks', SurgicalSleeveGastrectomy: 'Surgical risks, nutritional deficiencies' },
  { feature: 'Cost', EBG: 'Moderate, one-time', IntragastricBalloons: 'Moderate, may require replacement', GLP1Inhibitors: 'High, ongoing (up to $10,000/year)', SurgicalSleeveGastrectomy: 'High, one-time' },
];

const FAQs = [
  {
    question: 'How does EBG compare to surgical sleeve gastrectomy?',
    answer: 'EBG achieves a similar effect to surgical sleeve gastrectomy but without cutting or removing stomach tissue. This significantly reduces risks and recovery time while still providing effective weight loss.'
  },
  {
    question: 'Can EBG be reversed if necessary?',
    answer: 'While EBG is designed for long-term results, the procedure is potentially reversible, offering flexibility that surgical options cant match.'
  },
  {
    question: 'How soon can patients return to normal activities after EBG?',
    answer: 'Most patients can return to normal activities within 24-48 hours after the procedure.'
  },
  {
    question: 'Is EBG covered by insurance?',
    answer: 'Coverage varies by provider. As EBG gains recognition, more insurance companies are beginning to cover the procedure. We recommend checking with individual insurance providers.'
  },
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={false}
      animate={{ backgroundColor: isOpen ? "rgba(249, 250, 251, 1)" : "rgba(255, 255, 255, 1)" }}
      className="border border-gray-200 rounded-lg overflow-hidden mb-4"
    >
      <button
        className="flex justify-between items-center w-full text-left px-6 py-4"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-semibold pr-8">{question}</h3>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronDown className="flex-shrink-0 text-orange-500 w-6 h-6" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className="px-6 py-4 text-gray-600">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

function OBESITYProductPage() {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-24">
        <div className="container mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
          >
            Endolastic EBG: Revolutionary Obesity Treatment
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl mb-8 max-w-3xl"
          >
            Endoscopic Band Gastroplasty - Reshaping Lives Through Minimally Invasive Weight Loss
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a href="#video-demo" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-orange-600 bg-white hover:bg-orange-50 transition duration-300">
              <Play className="mr-2 h-5 w-5" />
              Watch Video
            </a>
          </motion.div>
        </div>
      </header>

      <main className="py-16">
        <div className="container mx-auto px-4">
          <section id="intro" className="mb-24">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Transforming Obesity Treatment with Minimally Invasive Technology</h2>
              <p className="text-xl text-gray-700 mb-8">
                The Endolastic Endoscopic Band Gastroplasty (EBG) system represents a breakthrough in the treatment of obesity. By leveraging advanced ligation technology, EBG offers a safe, effective, and minimally invasive solution for patients struggling with obesity, providing an alternative to traditional bariatric surgeries and pharmacological treatments.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="#how-it-works" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white bg-orange-600 hover:bg-orange-700 transition duration-300">
                  Learn How It Works
                </a>
                <a href="#clinical-outcomes" className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 transition duration-300">
                  View Clinical Outcomes
                </a>
              </div>
            </div>
          </section>

          <section id="video-demo" className="mb-24">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-6 text-center">See EBG in Action</h2>
              <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
                <iframe 
                  src="https://www.youtube.com/embed/LA21UHlSRwc?si=QphiYh1HdEDGNvA4" 
                  frameBorder="0" 
                  allow="autoplay; encrypted-media" 
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-xl shadow-2xl"
                ></iframe>
              </div>
            </div>
          </section>

          <section id="how-it-works" className="mb-24">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">How EBG Works</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <img
                    src={BandInUseImage}
                    alt="EBG Procedure Illustration"
                    className="rounded-xl shadow-lg w-full"
                  />
                </div>
                <div>
                  <ol className="space-y-6">
                    {[
                      { title: "Precision Targeting", description: "The EBG system targets the greater curvature of the stomach." },
                      { title: "Tissue Capture", description: "Using our proprietary SafeTrap™ barrels, the system captures large volumes of tissue, including the muscularis propria." },
                      { title: "Band Application", description: "Our unique WindowLock™ bands are applied to create controlled pressure zones along the gastric folds." },
                      { title: "Tissue Remodeling", description: "Over time, the banded tissue undergoes controlled pressure necrosis, leading to fibrosis and reshaping of the stomach." },
                      { title: "Volume Reduction", description: "The remodeled stomach takes on a sleeve-like structure, reducing its capacity and promoting earlier satiety." },
                    ].map((step, index) => (
                      <li key={index} className="flex items-start">
                        <span className="flex items-center justify-center flex-shrink-0 w-8 h-8 text-white bg-orange-500 rounded-full mr-4 font-bold">
                          {index + 1}
                        </span>
                        <div>
                          <h3 className="font-semibold text-lg mb-1">{step.title}</h3>
                          <p className="text-gray-600">{step.description}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </section>

          <section id="technical-specs" className="mb-24 bg-white py-16 rounded-2xl shadow-lg">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-3xl font-bold mb-12 text-center">Technical Specifications</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { label: "SafeTrap™ Barrel Design", value: "Optimized for deep tissue capture, including muscularis propria" },
                  { label: "WindowLock™ Band Material", value: "Proprietary biocompatible elastomer" },
                  { label: "Band Retention Force", value: "Engineered for long-term gastric tissue retention" },
                  { label: "Endoscope Compatibility", value: "Compatible with standard gastroscopes" },
                  { label: "Sterile Packaging", value: "Single-use devices, ethylene oxide sterilized" },
                ].map((spec, index) => (
                  <div key={index} className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">{spec.label}</h3>
                    <p className="text-gray-700">{spec.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="procedure-details" className="mb-24">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">Procedure Details</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { icon: "⏱️", label: "Duration", value: "30-45 minutes" },
                  { icon: "💉", label: "Anesthesia", value: "Conscious sedation" },
                  { icon: "🏠", label: "Recovery", value: "Same day discharge" },
                  { icon: "🔄", label: "Bands Applied", value: "5-7 bands" },
                  { icon: "📅", label: "Follow-up", value: "2 weeks, 3, 6, and 12 months" },
                ].map((detail, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center">
                    <span className="text-4xl mb-4 block">{detail.icon}</span>
                    <h3 className="font-semibold mb-2">{detail.label}</h3>
                    <p className="text-gray-600">{detail.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="clinical-outcomes" className="mb-24 bg-gradient-to-br from-orange-500 to-orange-600 text-white py-16 rounded-2xl">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-3xl font-bold mb-12 text-center">Clinical Outcomes</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { label: "Weight Loss", value: "Average 15-20% total body weight loss at 6 months" },
                  { label: "Excess Weight Loss", value: "Achieves >25% excess weight loss" },
                  { label: "Metabolic Improvement", value: "Significant improvements in obesity-related comorbidities" },
                  { label: "Quality of Life", value: "Marked improvement in obesity-related quality of life scores" },
                  { label: "Durability", value: "Sustained results observed in majority of patients at 2-year follow-up" },
                  { label: "Safety Profile", value: "Minimal complication rate, no reported severe adverse events" },
                ].map((outcome, index) => (
                  <div key={index} className="bg-white bg-opacity-10 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold mb-2">{outcome.label}</h3>
                    <p>{outcome.value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-12 text-center">
                <a href="#" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-orange-600 bg-white hover:bg-orange-50 transition duration-300">
                  <Download className="mr-2 h-5 w-5" />
                  Download Full Clinical Data
                </a>
              </div>
            </div>
          </section>

          <section id="patient-selection" className="mb-24">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">Patient Selection</h2>
              <p className="text-xl text-gray-700 mb-8">
                EBG is an ideal treatment option for individuals who meet the following criteria:
              </p>
              <ul className="list-disc list-inside space-y-4">
                <li>
                  <strong>BMI of 30 or higher:</strong> EBG is most effective for individuals with a significant amount of excess weight to lose.
                </li>
                <li>
                  <strong>Obesity-related comorbidities:</strong> EBG can help improve symptoms of obesity-related conditions such as type 2 diabetes, hypertension, and sleep apnea.
                </li>
                <li>
                  <strong>Desire for a minimally invasive procedure:</strong> EBG offers a less invasive alternative to traditional bariatric surgeries.
                </li>
                <li>
                  <strong>Commitment to lifestyle changes:</strong> While EBG can significantly aid weight loss, it is not a substitute for a healthy diet and regular exercise.
                </li>
              </ul>
            </div>
          </section>

          <section id="faq" className="mb-24">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {FAQs.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
              <div className="mt-8 text-center">
                <a
                  href="/faq/bmi"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white bg-orange-600 hover:bg-orange-700 transition duration-300"
                >
                  <ArrowRight className="mr-2 h-5 w-5" />
                  View Full FAQ
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

export default OBESITYProductPage;